<template>
  <div class="integrity-dashboard" :class="{ 'light-mode': !isDarkMode }">
    <!-- Top Section: Sidebar + Map -->
    <div class="dashboard-top">
      <!-- Left Sidebar - Command View -->
      <div class="command-sidebar">
        <div class="sidebar-header">
          <h2>COMMAND VIEW</h2>
        </div>
        
        <div class="metrics-section">
          <!-- Miles Monitored -->
          <div class="metric-block">
            <div class="metric-number">500</div>
            <div class="metric-label">Miles<br/>PIPELINE ZONES</div>
          </div>
          
          <!-- Active Alerts -->
          <div class="metric-block">
            <div class="metric-number">{{ activeAlertsCount }}</div>
            <div class="metric-label">ACTIVE ALERTS<br/>TODAY</div>
          </div>
          
          <!-- Health Percentage -->
          <div class="metric-block">
            <div class="metric-number">{{ healthPercentage }}%</div>
            <div class="metric-label">IN GOOD HEALTH</div>
          </div>
          
          <!-- Average Strain -->
          <div class="metric-block">
            <div class="metric-number">0.15</div>
            <div class="metric-label">AVG. STRAIN<br/>MICROEPSILON</div>
          </div>
        </div>
        
        <!-- Advanced Filter Section -->
        <div class="filter-section">
          <h3>Advanced Filters</h3>
          
          <!-- Health Status Filter -->
          <div class="filter-group">
            <label>Health Status</label>
            <v-select
              v-model="filters.healthStatus"
              :items="healthStatusOptions"
              item-text="text"
              item-value="value"
              multiple
              chips
              small-chips
              dense
              dark
              hide-details
            ></v-select>
          </div>
          
          <!-- Alert Status Filter -->
          <div class="filter-group">
            <label>Alert Status</label>
            <v-select
              v-model="filters.alertStatus"
              :items="alertStatusOptions"
              item-text="text"
              item-value="value"
              dense
              dark
              hide-details
            ></v-select>
          </div>
          
          <v-btn 
            @click="clearFilters" 
            small 
            outlined 
            color="cyan" 
            class="mt-3"
          >
            Clear Filters
          </v-btn>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content">
        <!-- Header -->
        <div class="main-header">
          <h1>Integrity Pulse</h1>
        </div>

        <!-- Scrollable Content Container -->
        <div class="scrollable-content">
          <!-- Content Layout: Full Width Map -->
          <div class="content-layout">
            <!-- Full Width Interactive Map -->
            <div class="map-section">
              <!-- Zone Selector at Top of Map -->
              <div class="map-controls">
                <div class="zone-selector-container">
                  <v-select
                    v-model="selectedZoneId"
                    :items="zoneSelectOptions"
                    item-text="label"
                    item-value="value"
                    label="Select Zone to View"
                    prepend-icon="mdi-map-marker"
                    clearable
                    dense
                    outlined
                    hide-details
                    class="zone-selector"
                    @change="onZoneSelectChange"
                  >
                    <template v-slot:item="{ item }">
                      <div class="zone-select-item">
                        <div class="zone-select-id">{{ item.text }}</div>
                        <div class="zone-select-details">
                          Mile {{ item.mileMarker }} • {{ item.status }} • {{ item.sensors }} sensors
                        </div>
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
              
              <div class="map-container-wrapper">
                <div style="height: 600px; position: relative;">
                  <MapboxPipelineMap
                    :key="`map-${filters.healthStatus.join('-')}`"
                    :is-dark-mode="isDarkMode"
                    :pipeline-data="filteredPipelineSegments"
                    :selected-zone="selectedZone"
                    @segment-selected="onSegmentSelected"
                    @zone-selected="onZoneSelected"
                    @zone-hover="onZoneHover"
                    @zone-hover-end="onZoneHoverEnd"
                    class="pipeline-map"
                    ref="pipelineMap"
                  />
                  
                  <!-- Zone Popup Overlay -->
                  <div 
                    v-if="zonePopup.show" 
                    class="zone-popup-card"
                    :style="zonePopupStyle"
                    @mouseenter="onPopupMouseEnter"
                    @mouseleave="onPopupMouseLeave"
                  >
                    <div class="zone-popup-header">
                      <h3 class="zone-title">{{ zonePopup.zone.name }}</h3>
                      <button 
                        class="close-btn"
                        @click="hideZonePopup"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div class="zone-popup-content">
                      <div class="zone-details-section">
                        <div class="zone-info-row">
                          <span class="zone-label">Zone ID:</span>
                          <span class="zone-value">{{ zonePopup.zone.id }}</span>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Mile Marker:</span>
                          <span class="zone-value">{{ zonePopup.zone.mileMarker }}</span>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Status:</span>
                          <v-chip 
                            x-small 
                            :color="getZoneStatusColor(zonePopup.zone.status)"
                            dark
                          >
                            {{ zonePopup.zone.status.toUpperCase() }}
                          </v-chip>
                        </div>
                        <div class="zone-info-row">
                          <span class="zone-label">Alerts:</span>
                          <span class="zone-value alert-count">{{ zonePopup.zone.alerts }}</span>
                        </div>
                      </div>
                      
                      <hr class="zone-divider" />
                      
                      <div class="sensors-section">
                        <h4 class="sensors-header">
                          Pipeline Sensors ({{ zonePopup.zone.sensors.length }})
                        </h4>
                        
                        <div class="sensors-list">
                          <div 
                            v-for="sensor in zonePopup.zone.sensors.slice(0, 6)" 
                            :key="sensor.id"
                            class="sensor-item"
                          >
                            <div class="sensor-info">
                              <div class="sensor-name">{{ sensor.name }}</div>
                              <div class="sensor-details">
                                <v-chip 
                                  x-small 
                                  outlined 
                                  :color="getPipelineSensorTypeColor(sensor.type)"
                                  class="sensor-type-chip"
                                >
                                  {{ sensor.type }}
                                </v-chip>
                                <span class="sensor-value">{{ sensor.value }}</span>
                              </div>
                            </div>
                            <v-icon 
                              small 
                              :color="getSensorStatusColor(sensor.status)"
                              class="sensor-status-icon"
                            >
                              {{ sensor.status === 'healthy' ? 'mdi-check-circle' : 
                                 sensor.status === 'warning' ? 'mdi-alert-circle' : 'mdi-close-circle' }}
                            </v-icon>
                          </div>
                          
                          <div 
                            v-if="zonePopup.zone.sensors.length > 6" 
                            class="more-sensors"
                          >
                            +{{ zonePopup.zone.sensors.length - 6 }} more sensors
                          </div>
                        </div>
                      </div>
                      
                      <hr class="zone-divider" />
                      
                      <div class="popup-actions">
                        <button 
                          class="view-all-btn"
                          @click="showAllPipelineData"
                        >
                          👁️ View All Pipeline Data
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Timeline Scrubber -->
              <div class="timeline-section">
                <div class="timeline-controls">
                  <v-btn icon :dark="isDarkMode" small>
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <span class="timeline-date">29 APR 2024</span>
                  <v-btn icon :dark="isDarkMode" small>
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
                
                <div class="timeline-bar">
                  <div class="timeline-track">
                    <div class="timeline-progress" :style="{ width: timelineProgress + '%' }"></div>
                  </div>
                  <div class="timeline-labels">
                    <span>6 MONTHS</span>
                    <span>30 MONTH</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Charts Section -->
          <div class="charts-section">
            <div class="chart-container">
              <div class="chart-header">
                <h3>Wall Thinning Detection</h3>
                <span class="chart-subtitle">Natural Frequency Shift</span>
              </div>
              <div class="chart-content">
                <div class="chart-value">0.15</div>
                <div class="mini-chart">
                  <svg viewBox="0 0 100 40" class="chart-svg">
                    <rect x="5" y="32" width="6" height="6" fill="#ff6600"/>
                    <rect x="15" y="28" width="6" height="10" fill="#ff6600"/>
                    <rect x="25" y="20" width="6" height="18" fill="#ff6600"/>
                    <rect x="35" y="25" width="6" height="13" fill="#ff6600"/>
                    <rect x="45" y="15" width="6" height="23" fill="#ff6600"/>
                    <rect x="55" y="18" width="6" height="20" fill="#ff6600"/>
                    <rect x="65" y="22" width="6" height="16" fill="#ff6600"/>
                    <rect x="75" y="12" width="6" height="26" fill="#ff6600"/>
                    <rect x="85" y="30" width="6" height="8" fill="#ff6600"/>
                  </svg>
                </div>
                <div class="chart-period">Resonant freq</div>
                <div class="chart-percentage">με</div>
              </div>
            </div>
            
            <div class="chart-container">
              <div class="chart-header">
                <h3>Structural Integrity</h3>
                <span class="chart-subtitle">Acoustic Emission</span>
              </div>
              <div class="chart-content">
                <div class="chart-value">2.3</div>
                <div class="mini-chart">
                  <svg viewBox="0 0 100 40" class="chart-svg">
                    <path d="M 5 35 L 15 30 L 25 35 L 35 20 L 45 35 L 55 25 L 65 35 L 75 15 L 85 35 L 95 30" 
                          stroke="#9c27b0" 
                          stroke-width="2" 
                          fill="none"/>
                    <circle cx="35" cy="20" r="2" fill="#e91e63"/>
                    <circle cx="75" cy="15" r="2" fill="#e91e63"/>
                  </svg>
                </div>
                <div class="chart-period">AE events</div>
                <div class="chart-percentage">kHz</div>
              </div>
            </div>
            
            <div class="chart-container">
              <div class="chart-header">
                <h3>Temperature</h3>
                <span class="chart-subtitle">Average</span>
              </div>
              <div class="chart-content">
                <div class="chart-value">72°F</div>
                <div class="mini-chart">
                  <svg viewBox="0 0 100 40" class="chart-svg">
                    <path d="M 5 30 Q 15 28 25 25 Q 35 22 45 20 Q 55 18 65 22 Q 75 25 85 23 Q 95 20 100 18" 
                          stroke="#ffaa00" 
                          stroke-width="2" 
                          fill="none"/>
                  </svg>
                </div>
                <div class="chart-period">5 months</div>
                <div class="chart-percentage">°F</div>
              </div>
            </div>
            
            <div class="chart-container">
              <div class="chart-header">
                <h3>Vibration Amplitude</h3>
                <span class="chart-subtitle">Stress & Pressure Waves</span>
              </div>
              <div class="chart-content">
                <div class="chart-value">0.002"</div>
                <div class="mini-chart">
                  <svg viewBox="0 0 100 40" class="chart-svg">
                    <rect x="8" y="25" width="5" height="13" fill="#00d4ff"/>
                    <rect x="18" y="20" width="5" height="18" fill="#00d4ff"/>
                    <rect x="28" y="15" width="5" height="23" fill="#00d4ff"/>
                    <rect x="38" y="10" width="5" height="28" fill="#00d4ff"/>
                    <rect x="48" y="18" width="5" height="20" fill="#00d4ff"/>
                    <rect x="58" y="22" width="5" height="16" fill="#00d4ff"/>
                    <rect x="68" y="28" width="5" height="10" fill="#00d4ff"/>
                    <rect x="78" y="12" width="5" height="26" fill="#00d4ff"/>
                    <rect x="88" y="30" width="5" height="8" fill="#00d4ff"/>
                  </svg>
                </div>
                <div class="chart-period">Strain signal</div>
                <div class="chart-percentage">inches</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Section: Full-Width Zones Table -->
    <div class="zones-table-section">
      <div class="zones-table-header">
        <h3>Pipeline Zones</h3>
        <div class="zones-table-controls">
          <span class="zones-count">{{ pipelineZones.length }} zones</span>
        </div>
      </div>
      
      <!-- Modern Data Table -->
      <div class="zones-table-wrapper">
        <v-data-table
          :headers="zoneTableHeaders"
          :items="pipelineZones"
          :items-per-page="25"
          class="zones-data-table elevation-0"
          @click:row="selectAndZoomToZone"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              small
              :color="getZoneStatusColor(item.status)"
              text-color="white"
            >
              {{ item.status.toUpperCase() }}
            </v-chip>
          </template>

          <template v-slot:[`item.alerts`]="{ item }">
            <div class="d-flex align-center">
              <v-icon
                small
                :color="item.alerts > 0 ? 'red' : 'green'"
              >
                {{ item.alerts > 0 ? "mdi-alert-circle" : "mdi-check-circle" }}
              </v-icon>
              <span class="ml-2" :class="{ 'error--text': item.alerts > 0 }">
                {{ item.alerts > 0 ? `${item.alerts} alerts` : "No alerts" }}
              </span>
            </div>
          </template>

          <template v-slot:[`item.sensors`]="{ item }">
            <div class="d-flex align-center">
              <v-icon small color="blue" class="mr-1">mdi-memory</v-icon>
              <span>{{ item.sensors }}</span>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn 
              x-small 
              outlined 
              color="primary" 
              @click.stop="selectAndZoomToZone(item)"
            >
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              View
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import MapboxPipelineMap from './MapboxPipelineMap.vue'

export default {
  name: 'AltLocationDetail',
  components: {
    MapboxPipelineMap
  },
  data() {
    return {
      // Search queries
      zoneSearchQuery: '',
      sensorSearchQuery: '',
      
      // Selected items
      selectedZone: null,
      selectedSensor: null,
      selectedSensorId: '1248',
      selectedSegment: 'Liberal',
      installDate: '08 MAR 2024',
      lastReport: '1 min ago',
      batteryLevel: '76',
      timelineProgress: 65,
      
      // UI State
      expandZoneDetails: false,
      
      // Advanced filters
      filters: {
        healthStatus: [ 'warning', 'critical'],
        alertStatus: 'all',
        batteryLevel: ['high', 'medium', 'low'],
        mileRange: [1, 400]
      },
      
      // Filter options
      healthStatusOptions: [
        { text: 'Healthy', value: 'healthy' },
        { text: 'Warning', value: 'warning' },
        { text: 'Critical', value: 'critical' }
      ],
      
      alertStatusOptions: [
        { text: 'All', value: 'all' },
        { text: 'Active Alerts', value: 'hasAlerts' },
        { text: 'No Alerts', value: 'noAlerts' }
      ],
      
      batteryLevelOptions: [
        { text: 'High (80-100%)', value: 'high' },
        { text: 'Medium (40-79%)', value: 'medium' },
        { text: 'Low (0-39%)', value: 'low' }
      ],
      
      // Data
      zoneData: [],
      pipelineSegments: [],
      searchResults: [],
      selectedSearchItem: null,
      sidebarSearchQuery: '',
      sensorStatusFilter: ['healthy', 'warning', 'critical'],
      zonePopup: {
        show: false,
        zone: null,
        mouseOver: false,
        triggeredByClick: false // Add flag to track how popup was triggered
      },
      zonePopupStyle: {},
      hoverTimeout: null,
      selectedZoneId: null,
      // Add map zones reference
      mapZones: [] // Store actual map zones for coordination
    }
  },
  
  computed: {
    isDarkMode() {
      return this.$vuetify.theme.dark
    },
    
    filteredZones() {
      let zones = this.zoneData
      
      // Apply search filters
      if (this.zoneSearchQuery) {
        const query = this.zoneSearchQuery.toLowerCase()
        zones = zones.filter(zone => 
          zone.zoneId.toLowerCase().includes(query) ||
          zone.mileMarker.toString().includes(query) ||
          zone.healthStatus.toLowerCase().includes(query)
        )
      }
      
      if (this.sensorSearchQuery) {
        const query = this.sensorSearchQuery.toLowerCase()
        zones = zones.filter(zone => 
          zone.sensors.some(sensor => 
            sensor.sensorId.toLowerCase().includes(query) ||
            sensor.reading.toString().includes(query)
          )
        )
      }
      
      // Apply advanced filters
      zones = zones.filter(zone => {
        // Hide Z002 specifically
        if (zone.zoneId === 'Z002') {
          return false
        }
        
        // Health status filter
        if (!this.filters.healthStatus.includes(zone.healthStatus)) {
          return false
        }
        
        // Alert status filter
        if (this.filters.alertStatus === 'hasAlerts' && zone.alerts.length === 0) {
          return false
        }
        if (this.filters.alertStatus === 'noAlerts' && zone.alerts.length > 0) {
          return false
        }
        
        // Mile range filter
        if (zone.mileMarker < this.filters.mileRange[0] || zone.mileMarker > this.filters.mileRange[1]) {
          return false
        }
        
        // Battery level filter
        const hasBatteryMatch = zone.sensors.some(sensor => {
          const batteryLevel = sensor.batteryLevel
          return this.filters.batteryLevel.some(level => {
            if (level === 'high' && batteryLevel >= 80) return true
            if (level === 'medium' && batteryLevel >= 40 && batteryLevel < 80) return true
            if (level === 'low' && batteryLevel < 40) return true
            return false
          })
        })
        
        if (!hasBatteryMatch) return false
        
        return true
      })
      
      return zones.slice(0, 100)
    },
    
    filteredZoneSensors() {
      if (!this.selectedZone) return []
      
      return this.selectedZone.sensors.filter(sensor => {
        return this.sensorStatusFilter.includes(sensor.status)
      })
    },
    
    activeAlertsCount() {
      return this.filteredZones.reduce((count, zone) => count + zone.alerts.length, 0)
    },
    
    healthPercentage() {
      const healthyZones = this.filteredZones.filter(zone => zone.healthStatus === 'healthy').length
      return this.filteredZones.length > 0 ? Math.round((healthyZones / this.filteredZones.length) * 100) : 0
    },
    
    searchableItems() {
      // Generate searchable items from actual data
      const items = []
      
      // Add zones
      this.zoneData.forEach(zone => {
        items.push({
          label: `Zone ${zone.zoneId}`,
          value: zone.zoneId,
          type: 'zone',
          status: zone.healthStatus,
          title: `Zone ${zone.zoneId}`,
          subtitle: `Mile ${zone.mileMarker} • ${zone.healthStatus} • ${zone.sensors.length} sensors`,
          data: zone
        })
      })
      
      // Add sensors from selected zones (limit to prevent performance issues)
      this.zoneData.slice(0, 10).forEach(zone => {
        zone.sensors.forEach(sensor => {
          items.push({
            label: `Sensor ${sensor.sensorId}`,
            value: sensor.sensorId,
            type: 'sensor',
            status: sensor.status,
            title: sensor.sensorId,
            subtitle: `${sensor.type} • ${sensor.status} • ${sensor.batteryLevel}%`,
            data: sensor
          })
        })
      })
      
      return items
    },
    
    pipelineZones() {
      // Use actual map zones if available, otherwise fall back to generated zones
      if (this.mapZones.length > 0) {
        const filteredMapZones = this.mapZones.filter(zone => {
          // Hide Z002 specifically
          if (zone.id === 'Z002') {
            return false
          }
          
          // Apply the same filters as filteredZones
          if (!this.filters.healthStatus.includes(zone.status)) {
            return false
          }
          
          if (this.filters.alertStatus === 'hasAlerts' && zone.alerts === 0) {
            return false
          }
          if (this.filters.alertStatus === 'noAlerts' && zone.alerts > 0) {
            return false
          }
          
          return true
        })
        
        console.log('📊 Using map zones for table:', filteredMapZones.length, 'zones')
        return filteredMapZones.slice(0, 50)
      }
      
      // Fallback to generated zones with calculated coordinates
      const fallbackZones = this.filteredZones.map(zone => ({
        id: zone.zoneId,
        mileMarker: zone.mileMarker,
        status: zone.healthStatus,
        alerts: zone.alerts.length,
        sensors: zone.sensors.length,
        coordinates: this.getZoneCoordinates(zone.mileMarker) // Always calculate coordinates
      })).filter(zone => {
        // Hide Z002 specifically
        return zone.id !== 'Z002'
      })
      
      console.log('📊 Using fallback zones for table:', fallbackZones.length, 'zones')
      return fallbackZones.slice(0, 50)
    },

    zoneSelectOptions() {
      // Use map zones if available for better coordination
      const zones = this.mapZones.length > 0 ? this.mapZones : this.pipelineZones
      
      return zones.map(zone => ({
        text: zone.id,
        value: zone.id,
        label: `${zone.id} - Mile ${zone.mileMarker}`,
        mileMarker: zone.mileMarker,
        status: zone.status,
        sensors: zone.sensors
      }))
    },

    zoneTableHeaders() {
      return [
        { text: 'Zone ID', value: 'id', sortable: true },
        { text: 'Mile Marker', value: 'mileMarker', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Alerts', value: 'alerts', sortable: true },
        { text: 'Sensors', value: 'sensors', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    },

    // Create pipeline segments from filtered zones for the map
    filteredPipelineSegments() {
      // Create realistic pipeline segments with zones distributed along the route
      const segments = [
        {
          id: 'segment-1',
          name: 'Amarillo to Liberal',
          healthStatus: 'healthy',
          coordinates: [
            [-101.8313, 35.2220], // Amarillo, TX
            [-101.2000, 35.8000],
            [-100.9000, 36.2000],
            [-100.5000, 36.6000],
            [-100.9203, 37.0431]  // Liberal, KS
          ],
          zones: []
        },
        {
          id: 'segment-2',
          name: 'Liberal to Dodge City',
          healthStatus: 'healthy',
          coordinates: [
            [-100.9203, 37.0431], // Liberal, KS
            [-100.5000, 37.4000],
            [-100.0000, 37.6000],
            [-99.9000, 37.7000],
            [-100.0167, 37.7528]  // Dodge City, KS
          ],
          zones: []
        },
        {
          id: 'segment-3',
          name: 'Dodge City to Garden City',
          healthStatus: 'warning',
          coordinates: [
            [-100.0167, 37.7528], // Dodge City, KS
            [-100.5000, 37.8000],
            [-100.8000, 37.9000],
            [-100.8731, 37.9717]  // Garden City, KS
          ],
          zones: []
        },
        {
          id: 'segment-4',
          name: 'Garden City to Hays',
          healthStatus: 'warning',
          coordinates: [
            [-100.8731, 37.9717], // Garden City, KS
            [-100.5000, 38.2000],
            [-100.0000, 38.4000],
            [-99.3000, 38.6000],
            [-99.3269, 38.8731]   // Hays, KS (ENDPOINT)
          ],
          zones: []
        }
      ]
      
      // Distribute filtered zones across segments based on mile markers
      this.filteredZones.forEach(zone => {
        const mileMarker = zone.mileMarker
        
        // Determine which segment this zone belongs to based on mile marker
        let segmentIndex = 0
        if (mileMarker <= 50) segmentIndex = 0      // 0-50 miles: segment-1
        else if (mileMarker <= 100) segmentIndex = 1 // 51-100 miles: segment-2
        else if (mileMarker <= 150) segmentIndex = 2 // 101-150 miles: segment-3
        else segmentIndex = 3                        // 151-200 miles: segment-4
        
        // Convert zone to map format
        const mapZone = {
          id: zone.zoneId,
          name: zone.zoneId,
          mileMarker: zone.mileMarker,
          status: zone.healthStatus,
          alerts: zone.alerts.length,
          sensors: zone.sensors.map(sensor => ({
            id: sensor.sensorId,
            name: sensor.name,
            type: sensor.type,
            value: `${sensor.reading.toFixed(sensor.type === 'movement' ? 3 : 2)} ${sensor.unit}`,
            status: sensor.status
          }))
        }
        
        segments[segmentIndex].zones.push(mapZone)
      })
      
      // Update segment health status based on zones
      segments.forEach(segment => {
        if (segment.zones.length > 0) {
          const criticalCount = segment.zones.filter(z => z.status === 'critical').length
          const warningCount = segment.zones.filter(z => z.status === 'warning').length
          const totalZones = segment.zones.length
          
          if (criticalCount / totalZones > 0.3) {
            segment.healthStatus = 'critical'
          } else if ((criticalCount + warningCount) / totalZones > 0.4) {
            segment.healthStatus = 'warning'
          } else {
            segment.healthStatus = 'healthy'
          }
        }
      })
      
      return segments
    }
  },
  
  created() {
    this.generateMockData()
  },
  
  mounted() {
    // Extract map zones after map is loaded
    this.$nextTick(() => {
      this.waitForMapAndExtractZones()
    })
    
    // Add click outside handler for popup
    document.addEventListener('click', this.handleClickOutside)
  },
  
  beforeDestroy() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside)
  },
  
  methods: {
    generateMockData() {
      // Generate zone data that aligns with the map's pipeline segments
      // Create zones every 5 miles along the 200-mile pipeline route
      this.zoneData = []
      
      const totalMiles = 200
      const zonesPerMile = 0.2 // One zone every 5 miles = 40 total zones
      const totalZones = Math.floor(totalMiles * zonesPerMile)
      
      for (let i = 0; i < totalZones; i++) {
        const mileMarker = (i * 5) + 5 // Start at mile 5, then 10, 15, 20, etc.
        const zoneId = `Z${(i + 1).toString().padStart(3, '0')}`
        
        // Determine health status based on mile position to match map segments
        let healthStatus = 'healthy'
        if (mileMarker <= 50) {
          // First segment: mostly healthy
          const rand = Math.random()
          if (rand < 0.85) healthStatus = 'healthy'
          else if (rand < 0.97) healthStatus = 'warning'
          else healthStatus = 'critical'
        } else if (mileMarker <= 100) {
          // Second segment: mostly healthy
          const rand = Math.random()
          if (rand < 0.90) healthStatus = 'healthy'
          else if (rand < 0.97) healthStatus = 'warning'
          else healthStatus = 'critical'
        } else if (mileMarker <= 150) {
          // Third segment: more warnings
          const rand = Math.random()
          if (rand < 0.30) healthStatus = 'healthy'
          else if (rand < 0.85) healthStatus = 'warning'
          else healthStatus = 'critical'
        } else {
          // Fourth segment: more critical
          const rand = Math.random()
          if (rand < 0.20) healthStatus = 'healthy'
          else if (rand < 0.50) healthStatus = 'warning'
          else healthStatus = 'critical'
        }
        
        // Generate 8-15 sensors per zone (more realistic than 53)
        const sensorCount = Math.floor(Math.random() * 8) + 8 // 8-15 sensors
        const sensors = Array.from({ length: sensorCount }, (_, sensorIndex) => {
          const sensorId = `S${mileMarker}-${(sensorIndex + 1).toString().padStart(2, '0')}`
          let status = 'healthy'
          const sensorRand = Math.random()
          if (sensorRand < 0.03) status = 'critical'
          else if (sensorRand < 0.15) status = 'warning'
          
          // Determine sensor type
          const sensorTypes = ['strain', 'temperature', 'movement']
          const sensorType = sensorTypes[sensorIndex % 3]
          
          // Generate realistic values based on sensor type
          let reading, unit
          switch (sensorType) {
            case 'strain':
              reading = status === 'critical' ? 0.25 + Math.random() * 0.05 : 
                       status === 'warning' ? 0.18 + Math.random() * 0.07 : 
                       0.08 + Math.random() * 0.08
              unit = 'με'
              break
            case 'temperature':
              reading = 65 + Math.random() * 13
              unit = '°F'
              break
            case 'movement':
              reading = status === 'critical' ? 0.005 + Math.random() * 0.002 : 
                       status === 'warning' ? 0.003 + Math.random() * 0.002 : 
                       0.001 + Math.random() * 0.002
              unit = '"'
              break
            default:
              reading = Math.random() * 100
              unit = 'units'
          }

          return {
            sensorId,
            status,
            batteryLevel: Math.floor(Math.random() * 100),
            reading: parseFloat(reading.toFixed(3)),
            unit,
            lastReport: Math.floor(Math.random() * 60) + 1,
            type: sensorType,
            name: `${sensorType.charAt(0).toUpperCase() + sensorType.slice(1)} Sensor ${sensorId}`,
            offsetX: (Math.random() - 0.5) * 20,
            offsetY: (Math.random() - 0.5) * 20,
            alerts: Math.random() < 0.1 ? [
              { 
                id: Date.now() + Math.random(),
                type: 'threshold',
                severity: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
                timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000)
              }
            ] : []
          }
        })
        
        this.zoneData.push({
          zoneId,
          mileMarker,
          healthStatus,
          sensors,
          alerts: Math.random() < 0.1 ? [
            { 
              type: ['strain', 'temperature', 'battery'][Math.floor(Math.random() * 3)], 
              severity: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
              timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000)
            }
          ] : []
        })
      }
      
      console.log(`📊 Generated ${this.zoneData.length} zones for miles 5-${totalMiles}`)
    },
    
    // New event handlers for Mapbox component
    onSegmentSelected(data) {
      // Convert segment data to zone format for compatibility
      const segment = data.segment
      const mockZone = {
        zoneId: segment.id,
        mileMarker: segment.sensors[0]?.mileMarker || 0,
        healthStatus: segment.healthStatus,
        sensors: segment.sensors.map(sensor => ({
          ...sensor,
          sensorId: sensor.id,
          batteryLevel: Math.floor(Math.random() * 100),
          reading: Math.random() * 100,
          lastReport: Math.floor(Math.random() * 60) + 1,
          type: ['strain', 'temperature', 'movement'][Math.floor(Math.random() * 3)],
          alerts: []
        })),
        alerts: []
      }
      
      this.selectZone(mockZone)
    },
    
    onZoneSelected(data) {
      const { zone, segment } = data
      
      // Show popup for clicked zone (different behavior than hover)
      this.zonePopup.show = true
      this.zonePopup.zone = zone
      this.zonePopup.triggeredByClick = true
      
      // Position popup at center of screen
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const popupWidth = 380
      const popupHeight = 500
      
      this.zonePopupStyle = {
        left: (screenWidth - popupWidth) / 2 + 'px',
        top: (screenHeight - popupHeight) / 2 + 'px',
        position: 'fixed'
      }
      
      // Also select the zone for the sidebar
      const mockZone = {
        zoneId: zone.id,
        mileMarker: zone.mileMarker,
        healthStatus: zone.status,
        sensors: zone.sensors.map(sensor => ({
          sensorId: sensor.id,
          name: sensor.name,
          type: sensor.type,
          reading: parseFloat(sensor.value.split(' ')[0]),
          unit: sensor.value.split(' ')[1],
          status: sensor.status,
          batteryLevel: Math.floor(Math.random() * 100),
          lastReport: Math.floor(Math.random() * 60) + 1,
          alerts: []
        })),
        alerts: []
      }
      
      this.selectZone(mockZone)
    },

    onZoneHover(data) {
      // Only show popup on hover if not already triggered by click
      if (this.zonePopup.triggeredByClick) {
        return
      }
      
      // Clear any existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
      
      // Add a small delay to prevent popup flickering
      this.hoverTimeout = setTimeout(() => {
        this.zonePopup.show = true
        this.zonePopup.zone = data.zone
        this.zonePopup.triggeredByClick = false
        
        // Position popup with offset to avoid covering the zone marker
        let left = data.x + 15  // Use screen coordinates directly
        let top = data.y - 10
        
        // Ensure popup doesn't go off-screen
        const popupWidth = 380
        const popupHeight = 500
        const screenWidth = window.innerWidth
        const screenHeight = window.innerHeight
        
        if (left + popupWidth > screenWidth) {
          left = data.x - popupWidth - 15
        }
        
        if (top + popupHeight > screenHeight) {
          top = data.y - popupHeight + 20
        }
        
        if (top < 0) {
          top = 10
        }
        
        if (left < 0) {
          left = 10
        }
        
        this.zonePopupStyle = {
          left: left + 'px',
          top: top + 'px',
          position: 'fixed'
        }
      }, 300) // 300ms delay
    },

    onZoneHoverEnd() {
      // Only hide hover-triggered popups
      if (this.zonePopup.triggeredByClick) {
        return
      }
      
      // Clear any pending timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
      
      // Add a small delay before hiding to allow moving to the popup
      setTimeout(() => {
        if (!this.zonePopup.mouseOver && !this.zonePopup.triggeredByClick) {
          this.zonePopup.show = false
        }
      }, 200)
    },
    
    onSensorSelected(data) {
      const { sensor, segment } = data
      
      // Create a mock zone for the sensor's segment
      const mockZone = {
        zoneId: segment.id,
        mileMarker: sensor.mileMarker,
        healthStatus: segment.healthStatus,
        sensors: segment.sensors,
        alerts: []
      }
      
      this.selectZone(mockZone)
      
      // Convert sensor data for compatibility
      const mockSensor = {
        ...sensor,
        sensorId: sensor.id,
        batteryLevel: Math.floor(Math.random() * 100),
        reading: Math.random() * 100,
        lastReport: Math.floor(Math.random() * 60) + 1,
        type: ['strain', 'temperature', 'movement'][Math.floor(Math.random() * 3)],
        alerts: []
      }
      
      this.selectSensor(mockSensor)
    },
    
    // Search functionality
    onSearchItemSelected(item) {
      if (!item) return
      
      if (item.type === 'zone') {
        this.selectZone(item.data)
      } else if (item.type === 'sensor') {
        // Find the zone containing this sensor
        const zone = this.zoneData.find(z => 
          z.sensors.some(s => s.sensorId === item.data.sensorId)
        )
        if (zone) {
          this.selectZone(zone)
          this.selectSensor(item.data)
        }
      }
    },
    
    // Interactive features
    selectZone(zone) {
      this.selectedZone = zone
      this.selectedSensor = null
      this.expandZoneDetails = true
    },
    
    selectSensor(sensor) {
      this.selectedSensor = sensor
      this.selectedSensorId = sensor.sensorId
      this.batteryLevel = sensor.batteryLevel.toString()
      this.lastReport = `${sensor.lastReport} min ago`
    },
    
    refreshSensorData() {
      // Simulate data refresh
      if (this.selectedZone) {
        this.selectedZone.sensors.forEach(sensor => {
          sensor.reading = Math.random() * 100
          sensor.lastReport = Math.floor(Math.random() * 60) + 1
        })
      }
    },
    
    // Utility methods for sensor readings
    formatSensorReading(sensor) {
      const value = sensor.reading.toFixed(sensor.type === 'movement' ? 3 : 2)
      const unit = sensor.unit || this.getSensorUnit(sensor.type)
      return `${value} ${unit}`
    },
    
    getSensorUnit(type) {
      const units = {
        strain: 'με',
        temperature: '°F',
        movement: '"'
      }
      return units[type] || 'units'
    },
    
    getBatteryStatusClass(batteryLevel) {
      if (batteryLevel >= 80) return 'battery-high'
      if (batteryLevel >= 40) return 'battery-medium'
      return 'battery-low'
    },
    
    getThresholdStatus(sensor) {
      const thresholds = {
        strain: { low: 0.05, high: 0.20 },   // με - deformation
        temperature: { low: 60, high: 80 },  // °F - thermal expansion
        movement: { low: 0.001, high: 0.004 } // " - displacement
      }
      
      const threshold = thresholds[sensor.type] || { low: 30, high: 70 }
      
      if (sensor.reading < threshold.low) return 'Below Normal'
      if (sensor.reading > threshold.high) return 'Above Normal'
      return 'Normal Range'
    },
    
    getZoneSensorStats(zone) {
      const stats = { healthy: 0, warning: 0, critical: 0 }
      zone.sensors.forEach(sensor => {
        stats[sensor.status]++
      })
      return stats
    },
    
    formatAlertTime(timestamp) {
      const now = new Date()
      const diff = now - new Date(timestamp)
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      
      if (hours > 0) return `${hours}h ${minutes}m ago`
      return `${minutes}m ago`
    },
    
    // Search functionality
    performSearch() {
      // Search is handled by computed property filteredZones
      // This method can be used for additional search logic if needed
    },
    
    clearFilters() {
      this.filters = {
        healthStatus: ['healthy', 'warning', 'critical'],
        alertStatus: 'all',
        batteryLevel: ['high', 'medium', 'low'],
        mileRange: [1, 400]
      }
      this.zoneSearchQuery = ''
      this.sensorSearchQuery = ''
      this.sensorStatusFilter = ['healthy', 'warning', 'critical']
    },
    
    showZonePopup(zone) {
      this.zonePopup.show = true
      this.zonePopup.zone = zone
      this.zonePopupStyle = {
        left: `${zone.offsetX}%`,
        top: `${zone.offsetY}%`
      }
    },
    
    hideZonePopup() {
      this.zonePopup.show = false
      this.zonePopup.triggeredByClick = false
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
    },
    
    onPopupMouseEnter() {
      this.zonePopup.mouseOver = true
    },
    
    onPopupMouseLeave() {
      this.zonePopup.mouseOver = false
      // Only auto-hide hover-triggered popups
      if (!this.zonePopup.triggeredByClick) {
        setTimeout(() => {
          if (!this.zonePopup.mouseOver && !this.zonePopup.triggeredByClick) {
            this.zonePopup.show = false
          }
        }, 200)
      }
    },
    
    showAllPipelineData() {
      // Show detailed pipeline data for the selected zone
      if (this.zonePopup.zone) {
        this.selectZone(this.zonePopup.zone)
        this.hideZonePopup()
      }
    },
    
    getZoneStatusColor(status) {
      const colors = {
        healthy: 'success',
        warning: 'warning',
        critical: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getPipelineSensorTypeColor(type) {
      const colors = {
        'strain': 'orange',         // Orange - strain gauges for wall thinning
        'temperature': 'red',       // Red - temperature probes
        'movement': 'purple'        // Purple - vibration/movement sensors
      }
      return colors[type] || 'grey'
    },
    
    getSensorStatusColor(status) {
      // Implement the logic to determine the sensor status color
    },
    
    getZoneCoordinates(mileMarker) {
      // Calculate coordinates based on mile marker along the pipeline route
      // Pipeline route: Amarillo, TX -> Liberal, KS -> Dodge City, KS -> Garden City, KS -> Hays, KS
      const pipelineRoute = [
        { mile: 0, coords: [-101.8313, 35.2220] },    // Amarillo, TX (start)
        { mile: 50, coords: [-100.9203, 37.0431] },   // Liberal, KS  
        { mile: 100, coords: [-100.0167, 37.7528] },  // Dodge City, KS
        { mile: 150, coords: [-100.8731, 37.9717] },  // Garden City, KS
        { mile: 200, coords: [-99.3269, 38.8731] }    // Hays, KS (end)
      ]
      
      // Find the two route points that bracket this mile marker
      let startPoint = pipelineRoute[0]
      let endPoint = pipelineRoute[pipelineRoute.length - 1]
      
      for (let i = 0; i < pipelineRoute.length - 1; i++) {
        if (mileMarker >= pipelineRoute[i].mile && mileMarker <= pipelineRoute[i + 1].mile) {
          startPoint = pipelineRoute[i]
          endPoint = pipelineRoute[i + 1]
          break
        }
      }
      
      // Interpolate between the two points
      const progress = (mileMarker - startPoint.mile) / (endPoint.mile - startPoint.mile)
      const longitude = startPoint.coords[0] + (endPoint.coords[0] - startPoint.coords[0]) * progress
      const latitude = startPoint.coords[1] + (endPoint.coords[1] - startPoint.coords[1]) * progress
      
      return [longitude, latitude]
    },
    
    selectAndZoomToZone(zone) {
      console.log('🔍 Selecting and zooming to zone:', {
        id: zone.id,
        mileMarker: zone.mileMarker,
        status: zone.status,
        coordinates: zone.coordinates
      })
      
      // Select the zone using existing logic
      const mockZone = {
        zoneId: zone.id,
        mileMarker: zone.mileMarker,
        healthStatus: zone.status,
        sensors: this.zoneData.find(z => z.zoneId === zone.id)?.sensors || [],
        alerts: this.zoneData.find(z => z.zoneId === zone.id)?.alerts || []
      }
      
      this.selectZone(mockZone)
      
      // Zoom the map to the zone coordinates using the actual coordinates from mapZones
      if (this.$refs.pipelineMap && this.$refs.pipelineMap.map) {
        let coordinates = zone.coordinates
        let coordinateSource = 'table-zone'
        
        // If coordinates not available, try to find the zone in mapZones
        if (!coordinates) {
          const mapZone = this.mapZones.find(mz => mz.id === zone.id)
          if (mapZone) {
            coordinates = mapZone.coordinates
            coordinateSource = 'map-zones'
          }
        }
        
        // If still no coordinates, calculate using the map's method
        if (!coordinates && this.$refs.pipelineMap.interpolateCoordinate) {
          // Find the appropriate segment for this zone
          const segment = this.$refs.pipelineMap.pipelineSegments.find(s => 
            s.zones.some(z => z.id === zone.id)
          )
          if (segment) {
            coordinates = this.$refs.pipelineMap.interpolateCoordinate(
              segment.coordinates,
              zone.mileMarker,
              200
            )
            coordinateSource = 'map-interpolation'
          }
        }
        
        // Final fallback to our own coordinate calculation
        if (!coordinates) {
          coordinates = this.getZoneCoordinates(zone.mileMarker)
          coordinateSource = 'fallback-calculation'
        }
        
        if (coordinates) {
          console.log('📍 Zone coordinate details:', {
            zoneId: zone.id,
            mileMarker: zone.mileMarker,
            coordinates: coordinates,
            source: coordinateSource,
            mapZonesCount: this.mapZones.length,
            mapSegmentsCount: this.$refs.pipelineMap.pipelineSegments?.length || 0
          })
          
          // Use a higher zoom level and add some easing for better UX
          this.$refs.pipelineMap.map.flyTo({
            center: coordinates,
            zoom: 11, // Reduced from 14 to 11 for better context view
            duration: 2000, // Slightly longer duration for smoother animation
            essential: true, // Ensures the animation completes
            padding: 80 // Increased padding for more context around the marker
          })
          
          // After zoom completes, highlight the specific zone marker
          setTimeout(() => {
            this.highlightZoneMarker(zone.id)
          }, 2200) // Wait for flyTo to complete
          
          console.log('🎯 Zoomed to zone:', zone.id, 'at coordinates:', coordinates, 'mile:', zone.mileMarker)
        } else {
          console.error('❌ Could not determine coordinates for zone:', zone.id, {
            tableZone: zone,
            mapZonesAvailable: this.mapZones.length,
            mapReady: !!this.$refs.pipelineMap?.map
          })
        }
      } else {
        console.error('❌ Map not available for zooming')
      }
    },
    
    // Method to highlight a specific zone marker after zoom
    highlightZoneMarker(zoneId) {
      if (!this.$refs.pipelineMap || !this.$refs.pipelineMap.map) return
      
      try {
        const map = this.$refs.pipelineMap.map
        const sourceId = `zone-${zoneId}`
        
        // Temporarily highlight the zone marker
        map.setFeatureState(
          { source: sourceId, id: zoneId },
          { selected: true }
        )
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          map.setFeatureState(
            { source: sourceId, id: zoneId },
            { selected: false }
          )
        }, 3000)
        
        console.log('✨ Highlighted zone marker:', zoneId)
      } catch (error) {
        console.warn('Failed to highlight zone marker:', zoneId, error)
      }
    },
    
    onZoneSelectChange(zoneId) {
      if (!zoneId) return

      // Find the zone data - try map zones first, then fallback to pipeline zones
      let zone = this.mapZones.find(z => z.id === zoneId)
      if (!zone) {
        zone = this.pipelineZones.find(z => z.id === zoneId)
      }
      
      if (!zone) return

      // Select the zone and zoom to it
      this.selectAndZoomToZone(zone)
    },
    // Extract zones from the map component to ensure coordination
    extractMapZones() {
      if (this.$refs.pipelineMap && this.$refs.pipelineMap.pipelineSegments) {
        const oldCount = this.mapZones.length
        this.mapZones = []
        
        this.$refs.pipelineMap.pipelineSegments.forEach(segment => {
          segment.zones.forEach(zone => {
            // Calculate coordinates using the map's interpolation method
            const coordinates = this.$refs.pipelineMap.interpolateCoordinate(
              segment.coordinates, 
              zone.mileMarker, 
              200 // Total pipeline miles as used in the map
            )
            
            this.mapZones.push({
              id: zone.id,
              mileMarker: zone.mileMarker,
              status: zone.status,
              alerts: zone.alerts,
              sensors: zone.sensors.length,
              coordinates: coordinates,
              segmentId: segment.id,
              // Store the full zone data for popup use
              fullZoneData: zone
            })
          })
        })
        
        console.log(`🔄 Extracted ${this.mapZones.length} zones (was ${oldCount})`)
        console.log('📍 Zone mile markers:', this.mapZones.map(z => `${z.id}(${z.mileMarker})`).join(', '))
        
        // Force reactivity update
        this.$forceUpdate()
      } else {
        // Map not ready yet, retry after a delay
        console.log('⏳ Map not ready, retrying zone extraction...')
        setTimeout(() => {
          this.extractMapZones()
        }, 1000)
      }
    },
    handleClickOutside(event) {
      // Only handle click-triggered popups
      if (!this.zonePopup.show || !this.zonePopup.triggeredByClick) {
        return
      }
      
      // Check if click is outside the popup
      const popup = document.querySelector('.zone-popup-card')
      if (popup && !popup.contains(event.target)) {
        this.hideZonePopup()
      }
    },
    // Wait for map to be fully ready before extracting zones
    waitForMapAndExtractZones(attempts = 0) {
      const maxAttempts = 10
      
      if (attempts >= maxAttempts) {
        console.warn('❌ Failed to extract map zones after maximum attempts')
        return
      }
      
      if (this.$refs.pipelineMap && 
          this.$refs.pipelineMap.pipelineSegments && 
          this.$refs.pipelineMap.pipelineSegments.length > 0 &&
          this.$refs.pipelineMap.map) {
        
        console.log(`✅ Map ready after ${attempts + 1} attempts, extracting zones...`)
        this.extractMapZones()
      } else {
        console.log(`⏳ Map not ready (attempt ${attempts + 1}/${maxAttempts}), retrying...`)
        setTimeout(() => {
          this.waitForMapAndExtractZones(attempts + 1)
        }, 1000)
      }
    }
  },
  watch: {
    selectedZone(newZone) {
      this.highlightZone(newZone)
    },
    mapMode(newMode) {
      this.updateMapView(newMode)
    },
    // Watch for map component updates
    '$refs.pipelineMap.pipelineSegments': {
      handler() {
        this.$nextTick(() => {
          this.extractMapZones()
        })
      },
      deep: true
    },
    // Watch for changes in filtered pipeline segments to re-extract zones
    filteredPipelineSegments: {
      handler() {
        this.$nextTick(() => {
          // Delay to ensure map has processed the new data
          setTimeout(() => {
            this.extractMapZones()
          }, 500)
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.integrity-dashboard {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--dashboard-bg);
  color: var(--dashboard-text);
  font-family: 'Roboto', sans-serif;
}

/* CSS Variables for theme switching */
.integrity-dashboard {
  --dashboard-bg: #0a0a0a;
  --dashboard-text: #ffffff;
  --sidebar-bg: #1a1a1a;
  --card-bg: #2d2d2d;
  --border-color: #333;
  --muted-text: #888;
  --search-bg: rgba(255, 255, 255, 0.1);
}

.integrity-dashboard.light-mode {
  --dashboard-bg: #f5f5f5;
  --dashboard-text: #333333;
  --sidebar-bg: #ffffff;
  --card-bg: #ffffff;
  --border-color: #e0e0e0;
  --muted-text: #666;
  --search-bg: rgba(0, 0, 0, 0.1);
}

/* Top Section: Sidebar + Map - Full Viewport Height */
.dashboard-top {
  display: grid;
  grid-template-columns: 280px 1fr;
  height: 100vh;
  overflow: hidden;
}

/* Left Sidebar - Command View */
.command-sidebar {
  background: var(--sidebar-bg);
  padding: 24px;
  border-right: 1px solid var(--border-color);
  overflow-y: auto;
  height: 100vh;
}

.sidebar-header h2 {
  color: var(--dashboard-text);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 32px 0;
  letter-spacing: 1px;
}

.metrics-section {
  margin-bottom: 48px;
}

.metric-block {
  margin-bottom: 32px;
}

.metric-number {
  font-size: 28px;
  font-weight: 300;
  color: var(--dashboard-text);
  line-height: 1;
  margin-bottom: 8px;
}

.metric-label {
  font-size: 12px;
  color: var(--muted-text);
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-section h3 {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 16px 0;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-group label {
  color: var(--dashboard-text);
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-group .v-select {
  margin-top: 8px;
}

/* Full-Width Zones Table Section - Below the Fold */
.zones-table-section {
  background: var(--sidebar-bg);
  border-top: 1px solid var(--border-color);
  min-height: 400px;
  flex-shrink: 0;
}

.zones-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--sidebar-bg);
  position: sticky;
  top: 0;
  z-index: 10;
}

.zones-table-header h3 {
  color: var(--dashboard-text);
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 1px;
}

.zones-table-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.zones-count {
  color: var(--muted-text);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.zones-table-container {
  background: var(--card-bg);
  margin: 0 24px 24px 24px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.zones-header {
  background: var(--sidebar-bg);
  border-bottom: 2px solid var(--border-color);
  padding: 0 24px;
}

.header-row {
  display: grid;
  grid-template-columns: 120px 140px 180px 100px 120px 120px;
  gap: 20px;
  align-items: center;
  padding: 16px 0;
}

.col-zone,
.col-mile,
.col-status,
.col-alerts,
.col-sensors,
.col-actions {
  font-weight: 600;
  color: var(--dashboard-text);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.zones-list {
  max-height: 500px;
  overflow-y: auto;
  padding: 0 24px;
}

.zone-row {
  display: grid;
  grid-template-columns: 120px 140px 180px 100px 120px 120px;
  gap: 20px;
  align-items: center;
  padding: 16px 0;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.zone-row:last-child {
  border-bottom: none;
}

.zone-row:hover {
  background: rgba(0, 212, 255, 0.05);
  transform: translateX(2px);
}

.zone-row.active {
  background: rgba(0, 212, 255, 0.1);
  border-left: 4px solid #00d4ff;
  margin-left: -24px;
  padding-left: 20px;
}

.zone-row.healthy {
  border-left: 4px solid #00ff88;
  margin-left: -24px;
  padding-left: 20px;
}

.zone-row.warning {
  border-left: 4px solid #ffaa00;
  margin-left: -24px;
  padding-left: 20px;
}

.zone-row.critical {
  border-left: 4px solid #ff6600;
  margin-left: -24px;
  padding-left: 20px;
}

.col-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #666;
}

.zone-row.healthy .status-indicator {
  background: #00ff88;
}

.zone-row.warning .status-indicator {
  background: #ffaa00;
}

.zone-row.critical .status-indicator {
  background: #ff6600;
}

.col-zone,
.col-mile,
.col-alerts,
.col-sensors {
  font-size: 14px;
  color: var(--dashboard-text);
  font-weight: 500;
}

.col-alerts {
  color: #ff6600;
  font-weight: 600;
}

/* Main Content Area */
.main-content {
  background: var(--dashboard-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid var(--border-color);
  background: var(--dashboard-bg);
  flex-shrink: 0;
}

.main-header h1 {
  color: var(--dashboard-text);
  font-size: 24px;
  font-weight: 400;
  margin: 0;
}

.search-section {
  display: flex;
  align-items: center;
}

.search-field {
  width: 300px;
}

.search-field >>> .v-input__control {
  background: var(--search-bg);
  border-radius: 4px;
}

.search-field >>> .v-text-field__details {
  display: none;
}

/* Scrollable Content Container */
.scrollable-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Content Layout */
.content-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - 300px); /* Prevent overflow into charts */
  overflow: hidden;
}

/* Map Section (Full Width) */
.map-section {
  display: flex;
  flex-direction: column;
  padding: 24px 32px;
  background: var(--dashboard-bg);
  min-height: 0;
  overflow: hidden;
  flex: 1;
}

.map-container-wrapper {
  position: relative;
  flex: 1;
}

.pipeline-map {
  flex: 1;
  background: var(--card-bg);
  border-radius: 8px;
  min-height: 400px;
  overflow: hidden;
}

/* Timeline Section */
.timeline-section {
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--dashboard-bg);
  flex-shrink: 0;
}

.timeline-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.timeline-date {
  color: var(--dashboard-text);
  font-size: 16px;
  font-weight: 500;
}

.timeline-bar {
  flex: 1;
  max-width: 400px;
  margin: 0 32px;
}

.timeline-track {
  height: 8px;
  background: var(--border-color);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.timeline-progress {
  height: 100%;
  background: linear-gradient(90deg, #00ff88 0%, #ffaa00 50%, #ff6600 100%);
  transition: width 0.3s ease;
}

.timeline-labels {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: var(--muted-text);
}

/* Right Sidebar - Sensor Details (Smaller) */
.sensor-sidebar {
  background: var(--dashboard-bg);
  padding: 24px 32px 24px 24px;
  border-left: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%; /* Constrain to parent height */
  overflow: hidden;
}

.sensor-header h2 {
  color: var(--dashboard-text);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 24px 0;
}

.sensor-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  color: var(--muted-text);
  font-size: 14px;
}

.detail-value {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 500;
}

/* Bottom Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--border-color);
  margin-top: 24px;
  flex-shrink: 0;
  position: relative;
  clear: both;
}

.chart-container {
  background: var(--card-bg);
  padding: 16px;
  min-height: 140px;
}

.chart-header {
  margin-bottom: 12px;
}

.chart-header h3 {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.chart-subtitle {
  color: var(--muted-text);
  font-size: 12px;
}

.chart-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chart-value {
  color: var(--dashboard-text);
  font-size: 18px;
  font-weight: 500;
}

.mini-chart {
  height: 40px;
}

.chart-svg {
  width: 100%;
  height: 100%;
}

.chart-period,
.chart-percentage {
  color: var(--muted-text);
  font-size: 11px;
}

/* Scrollbar styling */
.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: var(--dashboard-bg);
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: var(--muted-text);
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .integrity-dashboard {
    grid-template-columns: 240px 1fr;
  }
  
  .content-layout {
    grid-template-columns: 1fr 220px;
  }
  
  .metric-number {
    font-size: 36px;
  }
}

@media (max-width: 1200px) {
  .integrity-dashboard {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
  
  .command-sidebar {
    padding: 16px;
  }
  
  .content-layout {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }
  
  .sensor-sidebar {
    border-left: none;
    border-top: 1px solid var(--border-color);
  }
  
  .charts-section {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Filter Section Enhancements */
.filter-group {
  margin-bottom: 20px;
}

.filter-group label {
  color: var(--dashboard-text);
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-group .v-select {
  margin-top: 8px;
}

.mile-range-slider {
  margin: 8px 0;
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: var(--muted-text);
  margin-top: 4px;
}

/* Search Section Enhancements */
.search-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-field {
  width: 250px;
}

/* Enhanced Sensor Sidebar */
.sensor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.zone-details,
.sensor-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--muted-text);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 500;
}

/* Health Status Colors */
.detail-value.healthy {
  color: #00ff88;
}

.detail-value.warning {
  color: #000;
}

.detail-value.critical {
  color: #ff6600;
}

/* Sensor List */
.sensor-list {
  margin-top: 24px;
  max-height: 300px;
  overflow-y: auto;
}

.sensor-list h4 {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.sensor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  background: var(--card-bg);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.sensor-item:hover {
  background: rgba(0, 212, 255, 0.1);
  border-color: #00d4ff;
}

.sensor-item.active {
  background: rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
}

.sensor-id {
  font-size: 12px;
  font-weight: 600;
  color: var(--dashboard-text);
}

.sensor-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  text-transform: capitalize;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
}

.sensor-status.healthy .status-dot {
  background: #00ff88;
}

.sensor-status.warning .status-dot {
  background: #ffaa00;
}

.sensor-status.critical .status-dot {
  background: #ff6600;
}

.sensor-status.healthy {
  color: #00ff88;
}

.sensor-status.warning {
  color: #ffdd00;
}

.sensor-status.critical {
  color: #ff6600;
}

.sensor-battery {
  font-size: 11px;
  color: var(--muted-text);
}

/* Pipeline SVG Enhancements */
.pipeline-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.pipeline-map-wrapper {
  position: relative;
  flex: 1;
  background: var(--card-bg);
  border-radius: 8px;
  padding: 16px;
  min-height: 400px;
  overflow: hidden;
}

/* Scrollbar for sensor list */
.sensor-list::-webkit-scrollbar {
  width: 4px;
}

.sensor-list::-webkit-scrollbar-track {
  background: var(--dashboard-bg);
}

.sensor-list::-webkit-scrollbar-thumb {
  background: var(--muted-text);
  border-radius: 2px;
}

.sensor-list::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Vuetify Component Overrides */
.v-btn-toggle .v-btn {
  min-width: 60px !important;
}

.v-select .v-chip {
  margin: 2px !important;
}

.v-range-slider {
  margin-top: 16px !important;
}

/* Animation for expanding details */
.zone-details,
.sensor-details {
  transition: all 0.3s ease;
}

/* Light mode adjustments */
.integrity-dashboard.light-mode .map-tooltip {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  border-color: #ddd;
}

.integrity-dashboard.light-mode .tooltip-header {
  color: #0066cc;
}

/* Enhanced interactivity */
.interactive {
  transition: all 0.2s ease;
}

.interactive:hover {
  /* Removed transform scale, keeping only cursor pointer */
}

/* Status indicators in filters */
.v-chip.v-chip--outlined {
  border-color: currentColor !important;
}

/* Responsive adjustments for new elements */
@media (max-width: 1400px) {
  .search-field {
    width: 200px;
  }
  
  .sensor-sidebar {
    min-width: 200px;
  }
}

@media (max-width: 1200px) {
  .search-section {
    flex-direction: column;
    gap: 8px;
  }
  
  .search-field {
    width: 100%;
  }
  
  .map-controls {
    position: relative;
    top: auto;
    right: auto;
    margin-bottom: 16px;
    justify-content: space-between;
  }
  
  .filter-section {
    max-height: 300px;
    overflow-y: auto;
  }
}

@media (max-width: 768px) {
  .sensor-list {
    max-height: 200px;
  }
  
  .sensor-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .map-controls {
    flex-direction: column;
    gap: 8px;
  }
}

/* Sidebar Search Section */
.sidebar-search-section {
  margin-bottom: 24px;
}

.sidebar-search {
  width: 100%;
}

.search-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 12px;
}

.search-item-icon {
  flex-shrink: 0;
}

.search-item-content {
  flex: 1;
}

.search-item-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--dashboard-text);
}

.search-item-subtitle {
  font-size: 12px;
  color: var(--muted-text);
  margin-top: 2px;
}

.search-item-status {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
}

.status-indicator {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #666;
}

.search-item-status.healthy .status-indicator {
  background: #00ff88;
}

.search-item-status.warning .status-indicator {
  background: #ffaa00;
}

.search-item-status.critical .status-indicator {
  background: #ff6600;
}

/* Header Actions */
.header-actions {
  display: flex;
  gap: 8px;
}

/* Sensor Content Area */
.sensor-content-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* Allow flex shrinking */
  max-height: none; /* Remove fixed max-height, let parent constrain */
}

/* Alert Count Styling */
.alert-count.has-alerts {
  color: #ff6600 !important;
  font-weight: 600;
}

/* Zone Summary Stats */
.zone-summary {
  display: flex;
  gap: 16px;
  margin: 20px 0;
  padding: 16px;
  background: var(--card-bg);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.summary-item {
  flex: 1;
  text-align: center;
}

.summary-label {
  font-size: 11px;
  color: var(--muted-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
}

.summary-value.healthy {
  color: #00ff88;
}

.summary-value.warning {
  color: #ffdd00;
}

.summary-value.critical {
  color: #ff6600;
}

/* Sensors Section */
.sensors-section {
  margin-top: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.section-header h4 {
  color: var(--dashboard-text);
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.sensor-filters {
  display: flex;
  gap: 4px;
}

/* Sensor Readings List */
.sensor-readings-list {
  max-height: 400px;
  overflow-y: auto;
}

.sensor-reading-item {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sensor-reading-item:hover {
  background: rgba(0, 212, 255, 0.05);
  border-color: rgba(0, 212, 255, 0.3);
}

.sensor-reading-item.active {
  background: rgba(0, 212, 255, 0.1);
  border-color: #00d4ff;
  box-shadow: 0 0 0 1px rgba(0, 212, 255, 0.2);
}

.sensor-reading-item.critical {
  border-left: 4px solid #ff6600;
}

.sensor-reading-item.warning {
  border-left: 4px solid #ffaa00;
}

.sensor-reading-item.healthy {
  border-left: 4px solid #00ff88;
}

/* Sensor Reading Header */
.sensor-reading-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.sensor-info {
  flex: 1;
}

.sensor-id {
  font-size: 14px;
  font-weight: 600;
  color: var(--dashboard-text);
  margin-bottom: 2px;
}

.sensor-type {
  font-size: 11px;
  color: var(--muted-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sensor-status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.sensor-status-indicator .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.sensor-status-indicator.healthy {
  color: #00ff88;
}

.sensor-status-indicator.healthy .status-dot {
  background: #00ff88;
}

.sensor-status-indicator.warning {
  color: #ffdd00;
}

.sensor-status-indicator.warning .status-dot {
  background: #ffaa00;
}

.sensor-status-indicator.critical {
  color: #ff6600;
}

.sensor-status-indicator.critical .status-dot {
  background: #ff6600;
}

/* Sensor Reading Data */
.sensor-reading-data {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reading-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.reading-label {
  font-size: 12px;
  color: var(--muted-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.reading-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--dashboard-text);
}

.reading-value.battery-high {
  color: #00ff88;
}

.reading-value.battery-medium {
  color: #ffdd00;
}

.reading-value.battery-low {
  color: #ff6600;
}

/* Expanded Sensor Details */
.sensor-details-expanded {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.metrics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.metric-item {
  background: rgba(0, 212, 255, 0.05);
  padding: 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.1);
}

.metric-label {
  font-size: 10px;
  color: var(--muted-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--dashboard-text);
}

/* Alert History */
.alert-history {
  margin-top: 16px;
}

.alert-history h5 {
  font-size: 12px;
  color: var(--dashboard-text);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alert-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 102, 0, 0.1);
  border-radius: 4px;
  margin-bottom: 8px;
  border-left: 3px solid #ff6600;
}

.alert-type {
  font-size: 11px;
  font-weight: 600;
  color: #ff6600;
  text-transform: uppercase;
}

.alert-time {
  font-size: 10px;
  color: var(--muted-text);
}

.alert-severity {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alert-severity.low {
  background: rgba(255, 170, 0, 0.2);
  color: #ffdd00;
}

.alert-severity.medium {
  background: rgba(255, 102, 0, 0.2);
  color: #ff6600;
}

.alert-severity.high {
  background: rgba(255, 0, 0, 0.2);
  color: #ff0000;
}

/* Scrollbar for sensor content area */
.sensor-content-area::-webkit-scrollbar,
.sensor-readings-list::-webkit-scrollbar {
  width: 6px;
}

.sensor-content-area::-webkit-scrollbar-track,
.sensor-readings-list::-webkit-scrollbar-track {
  background: var(--dashboard-bg);
}

.sensor-content-area::-webkit-scrollbar-thumb,
.sensor-readings-list::-webkit-scrollbar-thumb {
  background: var(--muted-text);
  border-radius: 3px;
}

.sensor-content-area::-webkit-scrollbar-thumb:hover,
.sensor-readings-list::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Button toggle overrides */
.v-btn-toggle--dense .v-btn {
  min-width: 36px !important;
  height: 32px !important;
}

/* Responsive adjustments for new elements */
@media (max-width: 1400px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .zone-summary {
    flex-direction: column;
    gap: 12px;
  }
  
  .summary-item {
    text-align: left;
  }
}

@media (max-width: 1200px) {
  .sensor-content-area {
    max-height: calc(100vh - 400px);
  }
  
  .sensor-readings-list {
    max-height: 300px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .sensor-reading-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .alert-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* Zone Popup Styling */
.zone-popup-card {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0;
  min-width: 320px;
  max-width: 380px;
  z-index: 1000;
  animation: fadeInUp 0.2s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.08);
  pointer-events: auto;
  max-height: 500px;
  overflow-y: auto;
}

.zone-popup-card::before {
  content: '';
  position: absolute;
  top: 20px;
  left: -8px;
  border: 8px solid transparent;
  border-right-color: white;
  border-left: none;
}

.zone-popup-card::after {
  content: '';
  position: absolute;
  top: 19px;
  left: -10px;
  border: 9px solid transparent;
  border-right-color: rgba(0, 0, 0, 0.08);
  border-left: none;
  z-index: -1;
}

.zone-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px 16px;
  position: relative;
}

.zone-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: #1976d2;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #000;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  line-height: 1;
}

.close-btn:hover {
  background-color: #f5f5f5;
  color: #000;
}

.zone-popup-content {
  padding: 0 16px 16px 16px;
}

.zone-details-section {
  margin-bottom: 12px;
}

.zone-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 6px 0;
  font-size: 0.875rem;
}

.zone-label {
  color: #424242;
  font-weight: 500;
}

.zone-value {
  color: #1976d2;
  font-weight: 600;
}

.zone-value.alert-count {
  color: #f44336;
}

.zone-divider {
  margin: 12px 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

.sensors-section {
  margin-bottom: 12px;
}

.sensors-header {
  font-size: 1rem;
  font-weight: 500;
  color: #424242;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
}

.sensors-list {
  max-height: 250px;
  overflow-y: auto;
}

.sensor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin: 4px 0;
  background: #f5f5f5;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.sensor-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.sensor-item.active {
  background: rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
}

.sensor-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.sensor-name {
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
  color: #424242;
  margin-bottom: 2px;
}

.sensor-details {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sensor-type-chip {
  font-size: 0.7rem !important;
}

.sensor-value {
  font-family: monospace;
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
}

.sensor-status-icon {
  margin-left: 8px;
}

.more-sensors {
  text-align: center;
  padding: 8px;
  color: #757575;
  font-style: italic;
  font-size: 0.8rem;
}

.popup-actions {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.view-all-btn {
  width: 100%;
  padding: 8px 16px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background-color: #1565c0;
  transform: translateY(-1px);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile responsiveness for popup */
@media (max-width: 768px) {
  .zone-popup-card {
    min-width: 280px;
    max-width: 320px;
    max-height: 70vh;
  }
  
  .zone-title {
    font-size: 1.1rem;
  }
  
  .sensor-item {
    padding: 6px 8px;
  }
  
  .sensor-name {
    font-size: 0.75rem;
  }
  
  .sensor-details {
    gap: 4px;
  }
  
  .sensors-list {
    max-height: 200px;
  }
}

/* Modern Data Table */
.zones-table-wrapper {
  padding: 0 24px 24px 24px;
}

.zones-data-table {
  background: transparent !important;
}

.zones-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.zones-data-table >>> tbody tr {
  cursor: pointer;
  transition: all 0.2s ease;
}

.zones-data-table >>> tbody tr:hover {
  background-color: rgba(0, 212, 255, 0.05);
  transform: translateX(2px);
}

.zones-data-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  width: 6px;
}

.zones-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: var(--dashboard-bg);
}

.zones-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: var(--muted-text);
  border-radius: 3px;
}

.zones-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #777;
}
</style> 